'use client';

import { useEffect } from 'react';
import { useAdminScopeTree } from '@/hooks/use-admin-scope';
import { useScopeTree } from '@/hooks/use-scope-tree';
import { useAdminScopeStore } from '@/stores/admin-scope-store';
import { useScopeStore } from '@/stores/scope-store';

/**
 * Keeps both scope stores valid against the reachable tree. Mounted once in the
 * app shell so seeding applies app-wide, not only where a selector is rendered.
 *
 * - View selection (`scope-store`): prunes ids no longer reachable; when the
 *   result is empty, seeds with ALL configuration leaves.
 * - Admin scope (`admin-scope-store`): prunes against the adminable tree; when
 *   invalid/empty, seeds with the first CER and its first configuration.
 */
export function useScopeSync() {
	const { leaves, loading } = useScopeTree();
	const { tree: adminTree, loading: adminLoading } = useAdminScopeTree();
	const selectedOrgIds = useScopeStore((s) => s.selectedOrgIds);
	const setSelection = useScopeStore((s) => s.setSelection);
	const adminCerId = useAdminScopeStore((s) => s.cerId);
	const adminConfigId = useAdminScopeStore((s) => s.configId);
	const setAdminScope = useAdminScopeStore((s) => s.setScope);
	// View selection: prune, and seed to all leaves when nothing valid remains.
	useEffect(() => {
		if (loading || leaves.length === 0) return;
		const leafIds = new Set(leaves.map((l) => l.orgId));
		const valid = selectedOrgIds.filter((id) => leafIds.has(id));
		if (valid.length === 0) setSelection(leaves.map((l) => l.orgId));
		else if (valid.length !== selectedOrgIds.length) setSelection(valid);
	}, [loading, leaves, selectedOrgIds, setSelection]);

	// Admin scope: prune against the adminable tree, seed first CER/config.
	useEffect(() => {
		if (adminLoading || adminTree.length === 0) return;
		const cer = adminTree.find((c) => c.id === adminCerId);
		const configValid = cer
			? cer.configs.length === 0
				? adminConfigId === null
				: cer.configs.some((cfg) => cfg.id === adminConfigId)
			: false;
		if (cer && configValid) return;
		if (cer) {
			setAdminScope(cer.id, cer.configs[0]?.id ?? null);
		} else {
			const first = adminTree[0];
			setAdminScope(first.id, first.configs[0]?.id ?? null);
		}
	}, [adminLoading, adminTree, adminCerId, adminConfigId, setAdminScope]);
}
